import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Mail, Send, AlertCircle, Eye } from 'lucide-react';
import {
    sendEmailToAllUsers,
    sendEmailToProviders,
    sendEmailToNonProviders,
    sendEmailToUser,
    sendEmailToUsersList,
} from '../../services/controlPanelApi';
import Spinner from '../Spinner/Spinner';
import './EmailSender.css';

const TARGET_OPTIONS = [
    { value: 'all',           label: 'Todos los usuarios' },
    { value: 'providers',     label: 'Solo proveedores' },
    { value: 'non-providers', label: 'Usuarios no proveedores' },
    { value: 'user',          label: 'Email específico' },
    { value: 'users-list',    label: 'Lista de emails' },
];

const ensureProtocol = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
};

const isValidUrl = (value) => {
    if (!value) return true;
    try {
        const u = new URL(ensureProtocol(value));
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
        return false;
    }
};

const isValidEmail = (value) => {
    if (!value) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
};

const EmailSender = () => {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [buttonText, setButtonText] = useState('');
    const [buttonUrl, setButtonUrl] = useState('');

    const [targetAudience, setTargetAudience] = useState('all');
    const [email, setEmail] = useState('');
    const [emailsList, setEmailsList] = useState('');

    const [convertEnterToSlashN, setConvertEnterToSlashN] = useState(true);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const parsedEmails = useMemo(() => {
        if (!emailsList.trim()) return [];
        return emailsList.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.length > 0);
    }, [emailsList]);

    const invalidEmails = useMemo(
        () => parsedEmails.filter((e) => !isValidEmail(e)),
        [parsedEmails]
    );

    const normalizeMessage = (text) => {
        if (!convertEnterToSlashN) return text;
        return text.replace(/\r?\n/g, '\\n');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!subject.trim() || !message.trim()) {
            const msg = 'El asunto y el mensaje son obligatorios.';
            setError(msg); toast.error(msg);
            return;
        }
        if (buttonUrl && !isValidUrl(buttonUrl)) {
            const msg = 'La URL del botón no es válida. Debe comenzar con http:// o https://';
            setError(msg); toast.error(msg);
            return;
        }
        if (targetAudience === 'user' && !isValidEmail(email)) {
            const msg = 'Debés ingresar un email válido.';
            setError(msg); toast.error(msg);
            return;
        }
        if (targetAudience === 'users-list') {
            if (parsedEmails.length === 0) {
                const msg = 'Ingresá al menos un email.';
                setError(msg); toast.error(msg);
                return;
            }
            if (invalidEmails.length > 0) {
                const msg = `Hay ${invalidEmails.length} emails inválidos. Ejemplo: ${invalidEmails[0]}`;
                setError(msg); toast.error(msg);
                return;
            }
        }

        setLoading(true);
        try {
            const payloadBase = {
                subject: subject.trim(),
                message: normalizeMessage(message),
                buttonText: buttonText || undefined,
                buttonUrl: (() => {
                    const normalized = ensureProtocol(buttonUrl || '');
                    return normalized && normalized !== 'https://' ? normalized : undefined;
                })(),
            };

            let response;
            if (targetAudience === 'all')           response = await sendEmailToAllUsers(payloadBase);
            else if (targetAudience === 'providers')     response = await sendEmailToProviders(payloadBase);
            else if (targetAudience === 'non-providers') response = await sendEmailToNonProviders(payloadBase);
            else if (targetAudience === 'user')          response = await sendEmailToUser({ email: email.trim(), ...payloadBase });
            else if (targetAudience === 'users-list')    response = await sendEmailToUsersList({ emails: parsedEmails.join(','), ...payloadBase });

            const successMsg = (() => {
                if (targetAudience === 'user') return `Email enviado a ${email.trim()}`;
                if (targetAudience === 'users-list') return `Emails enviados a ${parsedEmails.length} destinatarios`;
                if (targetAudience === 'providers') return 'Emails enviados a proveedores';
                if (targetAudience === 'non-providers') return 'Emails enviados a usuarios no proveedores';
                return 'Emails enviados correctamente';
            })();

            toast.success((typeof response === 'string' && response) ? response : successMsg);

            setSubject(''); setMessage('');
            setButtonText(''); setButtonUrl('');
            setEmail(''); setEmailsList('');
            setTargetAudience('all');
        } catch (err) {
            console.error('Error al enviar emails:', err);
            const apiMsg = err?.response?.data || 'Error al enviar los emails.';
            const msg = typeof apiMsg === 'string' ? apiMsg : 'Error al enviar los emails.';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const submitLabel = (() => {
        if (loading) return 'Enviando…';
        if (targetAudience === 'user') return 'Enviar a este email';
        if (targetAudience === 'users-list') return 'Enviar a la lista';
        return 'Enviar email';
    })();

    return (
        <div className="cme-email">
            <header className="cme-email__header">
                <div className="cme-email__title-wrap">
                    <div className="cme-email__title-icon"><Mail size={20} /></div>
                    <div>
                        <h1 className="cme-email__title">Envío de emails</h1>
                        <p className="cme-email__subtitle">
                            Campañas masivas o envíos puntuales con CTA opcional. Vista previa al pie.
                        </p>
                    </div>
                </div>
            </header>

            {error && (
                <div className="cme-email__alert" role="alert">
                    <AlertCircle size={16} />
                    <span>{typeof error === 'string' ? error : 'Error al enviar el email.'}</span>
                </div>
            )}

            <div className="cme-email__layout">
                <form className="cme-email__form" onSubmit={handleSubmit}>
                    {loading && <Spinner />}

                    <div className="cme-email__field">
                        <label className="cme-email__label" htmlFor="cme-email-subject">Asunto</label>
                        <input
                            id="cme-email-subject"
                            type="text"
                            className="cme-email__input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Ej: Novedades de la semana"
                            required
                        />
                    </div>

                    <div className="cme-email__field">
                        <label className="cme-email__label" htmlFor="cme-email-body">Mensaje</label>
                        <textarea
                            id="cme-email-body"
                            className="cme-email__textarea"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Contenido del email"
                            rows={5}
                            required
                        />
                        <label className="cme-email__checkbox-row">
                            <input
                                type="checkbox"
                                checked={convertEnterToSlashN}
                                onChange={(e) => setConvertEnterToSlashN(e.target.checked)}
                            />
                            <span>
                                Convertir Enter a <code>\n</code>
                                <span className="cme-email__hint">
                                    {convertEnterToSlashN
                                        ? '— el backend interpretará los saltos de línea'
                                        : '— se enviarán los saltos tal cual'}
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="cme-email__row">
                        <div className="cme-email__field">
                            <label className="cme-email__label" htmlFor="cme-email-btntext">Texto del botón (opcional)</label>
                            <input
                                id="cme-email-btntext"
                                type="text"
                                className="cme-email__input"
                                value={buttonText}
                                onChange={(e) => setButtonText(e.target.value)}
                                placeholder="Ej: Ver más"
                            />
                        </div>
                        <div className="cme-email__field">
                            <label className="cme-email__label" htmlFor="cme-email-btnurl">URL del botón (opcional)</label>
                            <input
                                id="cme-email-btnurl"
                                type="text"
                                className="cme-email__input"
                                value={buttonUrl}
                                onChange={(e) => setButtonUrl(e.target.value)}
                                onBlur={() => {
                                    if (!buttonUrl) return;
                                    setButtonUrl(ensureProtocol(buttonUrl.trim()));
                                }}
                                placeholder="tu-dominio.com/accion"
                            />
                        </div>
                    </div>

                    <div className="cme-email__field">
                        <label className="cme-email__label" htmlFor="cme-email-target">Dirigido a</label>
                        <select
                            id="cme-email-target"
                            className="cme-email__input"
                            value={targetAudience}
                            onChange={(e) => setTargetAudience(e.target.value)}
                        >
                            {TARGET_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {targetAudience === 'user' && (
                        <div className="cme-email__field">
                            <label className="cme-email__label" htmlFor="cme-email-recipient">Email del destinatario</label>
                            <input
                                id="cme-email-recipient"
                                type="email"
                                className="cme-email__input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="destinatario@ejemplo.com"
                                required
                            />
                        </div>
                    )}

                    {targetAudience === 'users-list' && (
                        <div className="cme-email__field">
                            <label className="cme-email__label" htmlFor="cme-email-list">Lista de emails</label>
                            <textarea
                                id="cme-email-list"
                                className="cme-email__textarea"
                                value={emailsList}
                                onChange={(e) => setEmailsList(e.target.value)}
                                placeholder="Separados por coma, punto y coma, espacios o saltos de línea"
                                rows={3}
                                required
                            />
                            <span className="cme-email__hint">
                                {parsedEmails.length > 0
                                    ? `${parsedEmails.length} detectados${invalidEmails.length ? ` · ${invalidEmails.length} inválidos` : ''}`
                                    : 'Ej: uno@mail.com, dos@mail.com'}
                            </span>
                        </div>
                    )}

                    <div className="cme-email__actions">
                        <button type="submit" className="cme-email__submit-btn" disabled={loading}>
                            <Send size={16} />
                            <span>{submitLabel}</span>
                        </button>
                    </div>
                </form>

                <aside className="cme-email__preview" aria-label="Vista previa del email">
                    <div className="cme-email__preview-label">
                        <Eye size={14} />
                        <span>Vista previa</span>
                    </div>
                    <div className="cme-email__preview-card">
                        <div className="cme-email__preview-subject">
                            {subject || <span className="cme-email__preview-placeholder">Asunto del email</span>}
                        </div>
                        <div className="cme-email__preview-body">
                            {(message || 'Aquí verás una vista previa del contenido del email')
                                .split('\n')
                                .map((line, idx) => (
                                    <p key={idx} className="cme-email__preview-line">
                                        {line || ' '}
                                    </p>
                                ))}
                            {buttonText && buttonUrl && (
                                <div className="cme-email__preview-cta">
                                    <a
                                        href={ensureProtocol(buttonUrl)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="cme-email__preview-button"
                                    >
                                        {buttonText}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default EmailSender;
